/**
 * Jubly Email App — Gmail-style split pane + Intelligence views
 * Sidebar | Email List | Reading Pane (optional)
 * Top bar view tabs: List, Flows, Briefing, Billing, Todos
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import EmailList from '../components/email/EmailList';
import ComposeModal from '../components/email/ComposeModal';
import SettingsModal from '../components/email/SettingsModal';
import ExternalAccountModal from '../components/email/ExternalAccountModal';
import AccountSwitcher from '../components/email/AccountSwitcher';
import FlowsView from '../components/email/FlowsView';
import BriefingView from '../components/email/BriefingView';
import BillingView from '../components/email/BillingView';
import TodosView from '../components/email/TodosView';
import AppMailboxesView from '../components/email/AppMailboxesView';
import { useAuth } from '../hooks/useAuth.jsx';
import { useEmails, useFolders, useEmail } from '../hooks/useEmails';
import useKeyboardShortcuts from '../hooks/useKeyboardShortcuts';
import { loadShortcuts, SHORTCUT_LABELS } from '../hooks/defaultShortcuts';

const FOLDERS = [
  { key: 'inbox', label: 'Inbox', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  { key: 'starred', label: 'Starred', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' },
  { key: 'sent', label: 'Sent', icon: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8' },
  { key: 'drafts', label: 'Drafts', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
  { key: 'spam', label: 'Spam', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
  { key: 'all', label: 'All Mail', icon: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4' },
  { key: 'trash', label: 'Trash', icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' },
];

const VIEW_TABS = [
  { key: 'list', label: 'List', icon: 'M4 6h16M4 10h16M4 14h16M4 18h16' },
  { key: 'flows', label: 'Flows', icon: 'M4 6h16M4 12h8m-8 6h16' },
  { key: 'briefing', label: 'Briefing', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { key: 'billing', label: 'Billing', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { key: 'mailboxes', label: 'App mailboxes', icon: 'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4' },
  { key: 'todos', label: 'Todos', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
];

const EMPTY = {
  inbox: { title: "You're all caught up", sub: 'No new messages' },
  starred: { title: 'No starred messages', sub: 'Star emails to find them quickly' },
  sent: { title: 'No sent messages', sub: 'Messages you send appear here' },
  drafts: { title: 'No drafts', sub: 'Unsent messages appear here' },
  spam: { title: 'No spam!', sub: 'Spam is auto-detected' },
  all: { title: 'No mail yet', sub: 'All your mail appears here' },
  trash: { title: 'Trash is empty', sub: 'Deleted items appear here for 30 days' },
};

function ReadingPane({ emailId, onClose, onReply, onArchive, onDelete, onStar }) {
  const { email, loading } = useEmail(emailId);
  const [starred, setStarred] = useState(false);

  useEffect(() => { if (email) setStarred(email.is_starred); }, [email?.id]);

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-5 h-5 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" /></div>;
  if (!email) return <div className="flex items-center justify-center h-full text-sm text-gray-400">Email not found</div>;

  const from = typeof email.from_address === 'object' ? email.from_address : { email: email.from_address || '', name: '' };
  const to = Array.isArray(email.to_addresses) ? email.to_addresses : [];
  const date = email.sent_at || email.received_at || email.created_at;
  const formattedDate = date && !isNaN(new Date(date)) ? new Date(date).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  const bodyHtml = email.body?.html || email.body_html || '';
  const bodyText = email.body?.text || email.body_text || email.body_preview || '';

  const handleStar = () => { setStarred(!starred); onStar?.(email.id); };
  const handleArchive = () => { onArchive?.(email.id); onClose(); };
  const handleDelete = () => { onDelete?.(email.id); onClose(); };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-1 border-b border-gray-200 px-3 h-10 flex-shrink-0">
        <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg lg:hidden" title="Back">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
        </button>
        <button onClick={handleArchive} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg" title="Archive">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
        </button>
        <button onClick={handleDelete} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg" title="Delete">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
        </button>
        <button onClick={handleStar} className={`p-1.5 rounded-lg transition-colors ${starred ? 'text-amber-400 hover:text-amber-500' : 'text-gray-400 hover:text-amber-400 hover:bg-gray-100'}`} title="Star">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill={starred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2}>
            <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        </button>
        <div className="flex-1" />
        <span className="text-xs text-gray-400">{formattedDate}</span>
      </div>

      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">{email.subject || '(No Subject)'}</h2>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-semibold">{(from.name || from.email || '?')[0].toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">{from.name || from.email}</p>
            <p className="text-xs text-gray-500">to {to.map(t => t?.name || t?.email || t).join(', ') || 'me'}</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-4">
        {bodyHtml && !bodyHtml.startsWith('bodies/') ? (
          <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
        ) : bodyText && !bodyText.startsWith('bodies/') ? (
          <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{bodyText}</pre>
        ) : (
          <p className="text-sm text-gray-400 italic">No content</p>
        )}

        {(() => {
          const atts = typeof email.attachments === 'string' ? JSON.parse(email.attachments || '[]') : (email.attachments || []);
          if (atts.length === 0) return null;
          return (
            <div className="mt-4 border-t border-gray-100 pt-3">
              <p className="text-xs text-gray-500 mb-2 font-medium">{atts.length} attachment{atts.length > 1 ? 's' : ''}</p>
              <div className="flex flex-wrap gap-2">
                {atts.map((att, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    <span className="text-gray-700 truncate max-w-[200px]">{att.filename || att.name || 'attachment'}</span>
                    {(att.size || att.contentType) && (
                      <span className="text-[10px] text-gray-400">{att.size ? `${Math.round((att.size || 0) / 1024)}KB` : att.contentType}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      <div className="border-t border-gray-200 px-5 py-2.5 flex gap-2 flex-shrink-0 bg-gray-50/50">
        <button onClick={() => onReply?.(email, 'reply')} className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
          Reply
        </button>
        <button onClick={() => onReply?.(email, 'forward')} className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
          Forward
        </button>
      </div>
    </div>
  );
}

// ── Keyboard shortcuts cheat sheet (press ?) ────────────────────────────────
const SHORTCUT_GROUPS = [
  { title: 'Jump to', keys: ['goInbox', 'goStarred', 'goSent', 'goDrafts', 'goArchive'] },
  { title: 'Navigate', keys: ['nextEmail', 'prevEmail', 'openEmail', 'backToList', 'focusSearch'] },
  { title: 'Actions', keys: ['archive', 'trash', 'star', 'markRead', 'markUnread'] },
  { title: 'Compose', keys: ['compose', 'reply', 'replyAll', 'forward', 'sendEmail'] },
  { title: 'Other', keys: ['escape', 'showHelp'] },
];

function Kbd({ binding }) {
  // Render 'g+i' as two keys, 'Shift+I' with a plus, single keys plainly.
  const keys = (binding || '—').split('+');
  return (
    <span className="inline-flex items-center gap-1">
      {keys.map((k, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          {i > 0 && <span className="text-gray-400 text-[11px]">then</span>}
          <kbd className="min-w-[20px] text-center rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 text-[11px] font-mono text-gray-700 shadow-sm">
            {k === ' ' ? 'Space' : k}
          </kbd>
        </span>
      ))}
    </span>
  );
}

function ShortcutsHelp({ shortcuts, onClose, onCustomize }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[85vh] overflow-auto rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">Keyboard shortcuts</h2>
          <div className="flex items-center gap-3">
            <button onClick={onCustomize} className="text-sm font-medium text-blue-600 hover:text-blue-700">Customize →</button>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600" title="Close">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-x-10 gap-y-6 px-6 py-5 sm:grid-cols-2">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.title}>
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{group.title}</h3>
              <div className="space-y-1.5">
                {group.keys.map((action) => (
                  <div key={action} className="flex items-center justify-between gap-4">
                    <span className="text-[13px] text-gray-700">{SHORTCUT_LABELS[action] || action}</span>
                    <Kbd binding={shortcuts[action]} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-gray-100 px-6 py-3 text-[12px] text-gray-400">
          Press <kbd className="rounded border border-gray-300 bg-gray-50 px-1 py-0.5 font-mono text-[11px]">?</kbd> anytime to toggle this. Shortcuts pause while typing in a field.
        </div>
      </div>
    </div>
  );
}

export default function EmailApp() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFolder = searchParams.get('folder') || localStorage.getItem('jubly_mail_folder') || 'inbox';
  const { user } = useAuth();

  const [compose, setCompose] = useState(null);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState(() => searchParams.get('id') || null);
  const [showSettings, setShowSettings] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [currentAccount, setCurrentAccount] = useState('all');
  const [activeView, setActiveView] = useState(() => localStorage.getItem('jubly_mail_view') || 'list');
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const [shortcuts, setShortcuts] = useState(loadShortcuts);

  // Search box → debounced query fed to the email fetch.
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { emails, loading, loadingMore, total, hasMore, loadMore, refresh, starEmail, archiveEmail, deleteEmail, markAsRead, markAsUnread } = useEmails(activeFolder, { page: 1, limit: 50, search: debouncedSearch });
  const { folders } = useFolders();

  // Keyboard-navigation focus (j/k). Reset to top when the view changes.
  const [focusedIndex, setFocusedIndex] = useState(0);
  useEffect(() => { setFocusedIndex(0); }, [activeFolder, debouncedSearch]);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [settingsTab, setSettingsTab] = useState(null);
  // The email a shortcut acts on: the open one, else the keyboard-focused row.
  const targetEmail = emails.find((e) => e.id === selectedEmailId) || emails[focusedIndex] || null;
  const targetId = selectedEmailId || emails[focusedIndex]?.id || null;

  // Infinite scroll — the real scroll container is EmailApp's list pane, so the
  // handler must live here (not on EmailList's inner div, which never scrolls).
  const handleListScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (hasMore && !loadingMore && !loading && el.scrollHeight - el.scrollTop - el.clientHeight < 600) {
      loadMore();
    }
  }, [hasMore, loadingMore, loading, loadMore]);

  const isIntelligenceView = activeView !== 'list';

  function handleViewChange(view) {
    setActiveView(view);
    localStorage.setItem('jubly_mail_view', view);
  }

  // Close account menu on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setAccountMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateEmailParam = useCallback((emailId) => {
    setSelectedEmailId(emailId);
    const params = new URLSearchParams(searchParams);
    if (emailId) { params.set('id', emailId); } else { params.delete('id'); }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleFolderChange = useCallback((folder) => {
    localStorage.setItem('jubly_mail_folder', folder); // remember the last folder per user
    navigate(`/email?folder=${folder}`);
    setSelectedEmailId(null);
    setMobileSidebar(false);
  }, [navigate]);

  const handleSelectEmail = useCallback((email) => {
    updateEmailParam(email.id);
  }, [updateEmailParam]);

  const handleCompose = () => setCompose('compose');
  const handleReply = (email, mode = 'reply') => setCompose({ mode, replyTo: email });

  const initial = (user?.displayName || user?.emailAddress || '?')[0].toUpperCase();

  // Keyboard shortcuts
  const shortcutActions = useMemo(() => ({
    compose: handleCompose,
    reply: () => { if (targetEmail) handleReply(targetEmail, 'reply'); },
    replyAll: () => { if (targetEmail) handleReply(targetEmail, 'replyAll'); },
    forward: () => { if (targetEmail) handleReply(targetEmail, 'forward'); },
    escape: () => { if (compose) setCompose(null); else if (selectedEmailId) updateEmailParam(null); },
    archive: () => { if (targetId) { archiveEmail(targetId); if (selectedEmailId) updateEmailParam(null); } },
    trash: () => { if (targetId) { deleteEmail(targetId); if (selectedEmailId) updateEmailParam(null); } },
    star: () => { if (targetId) starEmail(targetId); },
    markRead: () => { if (targetId) markAsRead(targetId); },
    markUnread: () => { if (targetId) markAsUnread(targetId); },
    nextEmail: () => setFocusedIndex((i) => {
      const n = Math.min((i < 0 ? -1 : i) + 1, emails.length - 1);
      if (hasMore && n >= emails.length - 3) loadMore();
      return Math.max(0, n);
    }),
    prevEmail: () => setFocusedIndex((i) => Math.max(0, i - 1)),
    openEmail: () => { const em = emails[focusedIndex]; if (em) updateEmailParam(em.id); },
    goToFolder: (folder) => handleFolderChange(folder),
    focusSearch: () => document.querySelector('input[placeholder*="Search"]')?.focus(),
    backToList: () => updateEmailParam(null),
    showHelp: () => setShowShortcutsHelp((v) => !v),
  }), [compose, selectedEmailId, handleFolderChange, targetEmail, targetId, emails, focusedIndex, hasMore, loadMore, archiveEmail, deleteEmail, starEmail, markAsRead, markAsUnread, updateEmailParam]);

  useKeyboardShortcuts(shortcutActions, shortcuts, { composing: !!compose });

  const emptyConfig = EMPTY[activeFolder] || EMPTY.inbox;

  // Friendly title for the current view: system folder label, custom-folder
  // name, or a sensible fallback (handles 'all' and custom-folder UUIDs).
  const activeTitle = useMemo(() => {
    const sys = FOLDERS.find((f) => f.key === activeFolder);
    if (sys) return sys.label;
    const custom = folders.find((f) => f.id === activeFolder);
    if (custom) return custom.name;
    return activeFolder;
  }, [activeFolder, folders]);

  return (
    <div className="h-[calc(100vh-3.5rem)] flex">
      {/* Sidebar */}
      <aside className={`w-56 flex-shrink-0 bg-gray-50/60 flex flex-col border-r border-gray-200
        lg:translate-x-0 lg:static transition-transform duration-200
        ${mobileSidebar ? 'fixed inset-y-14 left-0 z-40 translate-x-0 shadow-xl bg-white' : 'max-lg:hidden'}`}>
        <div className="p-3 pb-1">
          <button onClick={handleCompose}
            className="flex items-center gap-3 rounded-2xl bg-white border border-gray-200 px-5 py-3 text-sm font-medium text-gray-700 shadow-sm hover:shadow-md hover:bg-gray-50 transition-all w-auto">
            <svg className="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"/></svg>
            Compose
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-2">
          {FOLDERS.map(({ key, label, icon }) => (
            <button key={key} onClick={() => handleFolderChange(key)}
              className={`w-full flex items-center gap-3 rounded-r-full rounded-l-full px-3 py-[7px] mb-[1px] text-[13px] font-medium transition-colors
                ${activeFolder === key ? 'bg-blue-100/80 text-blue-800' : 'text-gray-700 hover:bg-gray-200/60'}`}>
              <svg className={`w-[18px] h-[18px] flex-shrink-0 ${activeFolder === key ? 'text-blue-700' : 'text-gray-500'}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
              </svg>
              <span className="flex-1 text-left">{label}</span>
            </button>
          ))}

          {/* Custom folders / migrated Gmail labels (e.g. Sara, Amazon) */}
          {folders.filter((f) => f.type === 'custom').length > 0 && (
            <div className="mt-3 pt-2 border-t border-gray-200/70">
              <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Labels</p>
              {folders.filter((f) => f.type === 'custom').map((f) => (
                <button key={f.id} onClick={() => handleFolderChange(f.id)}
                  className={`w-full flex items-center gap-3 rounded-r-full rounded-l-full px-3 py-[7px] mb-[1px] text-[13px] font-medium transition-colors
                    ${activeFolder === f.id ? 'bg-blue-100/80 text-blue-800' : 'text-gray-700 hover:bg-gray-200/60'}`}>
                  <svg className={`w-[18px] h-[18px] flex-shrink-0 ${activeFolder === f.id ? 'text-blue-700' : 'text-gray-500'}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 8V4a1 1 0 011-1z" />
                  </svg>
                  <span className="flex-1 text-left truncate">{f.name}</span>
                </button>
              ))}
            </div>
          )}
        </nav>

        {/* Account area — bottom left */}
        <div className="p-3 border-t border-gray-200 relative" ref={accountMenuRef}>
          {/* Account menu popover (opens upward) */}
          {accountMenuOpen && (
            <div className="absolute bottom-full left-1 right-1 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 max-h-72 overflow-y-auto">
              <AccountSwitcher
                currentAccount={currentAccount}
                onAccountChange={(acct) => { setCurrentAccount(acct); setAccountMenuOpen(false); }}
                onAddAccount={() => { setShowAddAccount(true); setAccountMenuOpen(false); }}
                inline
              />
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={() => { setShowSettings(true); setAccountMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                Settings
              </button>
            </div>
          )}

          <button
            onClick={() => setAccountMenuOpen(!accountMenuOpen)}
            className="flex w-full items-center gap-2 rounded-lg p-1 hover:bg-gray-200/60 transition-colors"
          >
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-[10px] font-semibold">{initial}</span>
            </div>
            <div className="min-w-0 flex-1 text-left">
              <p className="truncate text-xs font-medium text-gray-800">{user?.displayName}</p>
              <p className="truncate text-[10px] text-gray-400">{user?.emailAddress}</p>
            </div>
            <svg className={`w-3.5 h-3.5 flex-shrink-0 text-gray-400 transition-transform ${accountMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
        </div>
      </aside>

      {mobileSidebar && <div className="fixed inset-0 z-30 bg-black/20 lg:hidden" onClick={() => setMobileSidebar(false)} />}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar with view tabs */}
        <div className="flex items-center gap-2 border-b border-gray-200 px-3 h-10 flex-shrink-0 bg-white">
          <button onClick={() => setMobileSidebar(!mobileSidebar)} className="lg:hidden p-1 text-gray-500 hover:bg-gray-100 rounded">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>

          {/* View tabs */}
          <div className="flex items-center gap-0.5">
            {VIEW_TABS.map(({ key, label, icon }) => (
              <button
                key={key}
                onClick={() => handleViewChange(key)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[13px] font-medium transition-colors ${
                  activeView === key
                    ? 'bg-blue-100/80 text-blue-800'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                }`}
              >
                <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={icon} />
                </svg>
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          <div className="flex-1" />

          {/* Search + refresh (only in list view) */}
          {activeView === 'list' && (
            <>
              <button onClick={refresh} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded" title="Refresh">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              </button>
              <div className="hidden md:block w-48">
                <div className="relative">
                  <input type="text" placeholder="Search mail"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full pl-7 pr-3 py-1 text-[13px] bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors placeholder:text-gray-400" />
                  <svg className="absolute left-2 top-[6px] w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Content */}
        {activeView === 'mailboxes' ? (
          <div className="flex-1 overflow-hidden bg-white">
            <AppMailboxesView />
          </div>
        ) : isIntelligenceView ? (
          <div className="flex-1 overflow-y-auto bg-white">
            {activeView === 'flows' && <FlowsView account={currentAccount} />}
            {activeView === 'briefing' && <BriefingView account={currentAccount} />}
            {activeView === 'billing' && <BillingView />}
            {activeView === 'todos' && <TodosView />}
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {/* Email List */}
            <div className={`flex flex-col border-r border-gray-200 bg-white ${selectedEmailId ? 'hidden lg:flex lg:w-[420px] flex-shrink-0' : 'flex-1'}`}>
              <div className="flex items-center gap-2 border-b border-gray-200 px-3 h-8 flex-shrink-0">
                <div className="flex items-baseline gap-2">
                  <h2 className="text-sm font-semibold text-gray-900 capitalize">{activeTitle}</h2>
                  {total > 0 && (
                    <span className="text-xs text-gray-400 font-normal">{total.toLocaleString()} {total === 1 ? 'message' : 'messages'}</span>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto" onScroll={handleListScroll}>
                {loading ? (
                  <div className="flex justify-center py-12"><div className="w-5 h-5 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" /></div>
                ) : emails.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center px-6 py-16">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={0.75}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 mb-1">{emptyConfig.title}</h3>
                    <p className="text-xs text-gray-500">{emptyConfig.sub}</p>
                  </div>
                ) : (
                  <EmailList
                    emails={emails}
                    loading={false}
                    folder={activeFolder}
                    activeId={selectedEmailId}
                    focusedIndex={focusedIndex}
                    onSelectEmail={handleSelectEmail}
                    onStar={starEmail}
                    onLoadMore={loadMore}
                    hasMore={hasMore}
                    loadingMore={loadingMore}
                  />
                )}
              </div>
            </div>

            {/* Reading Pane */}
            {selectedEmailId ? (
              <div className="flex-1 min-w-0 bg-white">
                <ReadingPane
                  emailId={selectedEmailId}
                  onClose={() => updateEmailParam(null)}
                  onReply={handleReply}
                  onArchive={(id) => { archiveEmail?.(id); updateEmailParam(null); }}
                  onDelete={(id) => { deleteEmail?.(id); updateEmailParam(null); }}
                  onStar={starEmail}
                />
              </div>
            ) : (
              <div className="hidden lg:flex flex-1 items-center justify-center bg-gray-50/50">
                <div className="text-center">
                  <svg className="w-12 h-12 text-gray-200 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={0.75}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm text-gray-400">Select an email to read</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Compose */}
      {compose && (
        <ComposeModal
          mode={typeof compose === 'object' ? compose.mode : 'compose'}
          replyTo={typeof compose === 'object' ? compose.replyTo : null}
          from={user?.emailAddress}
          onClose={() => setCompose(null)}
          onSent={() => { setCompose(null); refresh(); }}
        />
      )}

      {/* Settings */}
      {showSettings && (
        <SettingsModal
          initialTab={settingsTab}
          onClose={() => { setShowSettings(false); setSettingsTab(null); }}
          onShortcutsChange={(sc) => setShortcuts(sc)}
        />
      )}

      {/* Keyboard shortcuts cheat sheet (press ?) */}
      {showShortcutsHelp && (
        <ShortcutsHelp
          shortcuts={shortcuts}
          onClose={() => setShowShortcutsHelp(false)}
          onCustomize={() => { setShowShortcutsHelp(false); setSettingsTab('shortcuts'); setShowSettings(true); }}
        />
      )}

      {/* Add External Account */}
      <ExternalAccountModal
        isOpen={showAddAccount}
        onClose={() => setShowAddAccount(false)}
        onAccountAdded={() => setShowAddAccount(false)}
      />
    </div>
  );
}
